// Shared autoplay + progress bar behaviour for looping Splide sliders.
// Call before `slider.mount()`. Returns `destroy()`, which removes every
// listener, observer and timer — call it before destroying the slider.
//
// The progress bar is an invisible native range (input) on top of a visual
// thumb: the range provides pointer, touch and keyboard input, the thumb is
// moved with `transform` so it can animate together with the slides.
export const initSliderControls = ({
  section,
  slider,
  slides,
  progressBar,
  progressThumb,
  interval = 4000,
  speed = 700,
  getSlideLabel = () => '',
  loop = slides.length > 1,
}) => {
  const slideCount = slides.length;
  const canLoop = loop;
  const listeners = new AbortController();
  const { signal } = listeners;
  let observer = null;
  let destroyed = false;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const hoverCapable = window.matchMedia('(hover: hover)');
  let isVisible = !('IntersectionObserver' in window);
  let dragging = false;
  let scrubbing = false;
  let followPointer = false;
  let pointerStartX = 0;
  let autoplayTimer = null;

  const clearAutoplay = () => {
    window.clearTimeout(autoplayTimer);
    autoplayTimer = null;
  };
  const canAutoplay = () =>
    !destroyed &&
    canLoop &&
    isVisible &&
    !dragging &&
    !scrubbing &&
    !document.hidden &&
    !reducedMotion.matches &&
    !(hoverCapable.matches && section.matches(':hover')) &&
    !section.querySelector(':focus-visible');
  const scheduleAutoplay = () => {
    clearAutoplay();
    if (!canAutoplay()) return;

    autoplayTimer = window.setTimeout(() => {
      autoplayTimer = null;
      if (canAutoplay()) slider.go('>');
      scheduleAutoplay();
    }, interval);
  };

  const renderProgress = (value = slider.index) => {
    if (!progressThumb) return;

    progressThumb.style.transform = `translateX(${value * 100}%)`;
  };

  const syncProgressBar = () => {
    if (!progressBar || scrubbing) return;

    const label = getSlideLabel(slides[slider.index]);

    progressBar.value = `${slider.index}`;
    progressBar.setAttribute(
      'aria-valuetext',
      `${slider.index + 1} / ${slideCount}${label ? `: ${label}` : ''}`
    );
    renderProgress();
  };

  // The `options` setter needs mounted components, so before `mount()` write
  // into the options object directly.
  slider.options.speed = reducedMotion.matches ? 0 : speed;

  if (progressThumb) {
    progressThumb.style.width = `${100 / slideCount}%`;
  }

  // `move` fires with the target index, so the thumb travels together with
  // the slides instead of catching up after the transition ends.
  slider.on('mounted move', syncProgressBar);
  slider.on('mounted moved', scheduleAutoplay);
  slider.on('drag', () => {
    dragging = true;
    clearAutoplay();
  });
  slider.on('dragged', () => {
    dragging = false;
    scheduleAutoplay();
  });

  if (progressBar) {
    progressBar.max = `${slideCount - 1}`;
    progressBar.disabled = !canLoop;
    // Native thumb is invisible but sized like the visual one, so grabbing
    // the visible thumb maps 1:1 onto the range value.
    progressBar.style.setProperty(
      '--slider-progress-thumb-width',
      `${100 / slideCount}%`
    );

    // While following the pointer the thumb transition is disabled; otherwise
    // it animates to the target, e.g. after a click on the empty track.
    const setFollowPointer = (value) => {
      followPointer = value;
      section.dataset.sliderScrubbing = `${value}`;
    };

    const stopScrubbing = () => {
      if (!scrubbing) return;

      scrubbing = false;
      setFollowPointer(false);
      slider.go(Math.round(Number(progressBar.value)));
      syncProgressBar();
      scheduleAutoplay();
    };

    progressBar.addEventListener(
      'pointerdown',
      (event) => {
        const thumbRect = progressThumb?.getBoundingClientRect();

        scrubbing = true;
        pointerStartX = event.clientX;
        setFollowPointer(
          !thumbRect ||
            (event.clientX >= thumbRect.left &&
              event.clientX <= thumbRect.right)
        );
        clearAutoplay();
      },
      { signal }
    );
    progressBar.addEventListener(
      'pointermove',
      (event) => {
        if (!scrubbing || followPointer) return;
        if (Math.abs(event.clientX - pointerStartX) < 4) return;

        // A click on the track turned into a drag: hand the thumb to the pointer.
        setFollowPointer(true);
        renderProgress(Number(progressBar.value));
      },
      { signal }
    );
    window.addEventListener('pointerup', stopScrubbing, { signal });
    window.addEventListener('pointercancel', stopScrubbing, { signal });

    progressBar.addEventListener(
      'input',
      () => {
        const value = Number(progressBar.value);
        const index = Math.round(value);

        // The thumb follows the pointer 1:1 when dragged and glides to the
        // nearest slide on a track click; the slides step one at a time so the
        // track never lands between two of them.
        renderProgress(followPointer ? value : index);
        if (index !== slider.index) slider.go(index);
      },
      { signal }
    );

    progressBar.addEventListener(
      'keydown',
      (event) => {
        const targets = {
          ArrowLeft: slider.index - 1,
          ArrowDown: slider.index - 1,
          ArrowRight: slider.index + 1,
          ArrowUp: slider.index + 1,
          Home: 0,
          End: slideCount - 1,
        };
        if (!(event.key in targets)) return;

        event.preventDefault();
        slider.go(Math.max(0, Math.min(slideCount - 1, targets[event.key])));
      },
      { signal }
    );
  }

  section.addEventListener('pointerenter', clearAutoplay, { signal });
  section.addEventListener('pointerleave', scheduleAutoplay, { signal });
  section.addEventListener(
    'focusin',
    () => window.setTimeout(scheduleAutoplay),
    { signal }
  );
  section.addEventListener(
    'focusout',
    () => window.setTimeout(scheduleAutoplay),
    { signal }
  );
  document.addEventListener('visibilitychange', scheduleAutoplay, { signal });
  reducedMotion.addEventListener?.(
    'change',
    () => {
      slider.options = { speed: reducedMotion.matches ? 0 : speed };
      scheduleAutoplay();
    },
    { signal }
  );

  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(
      ([entry]) => {
        isVisible = entry.intersectionRatio >= 0.25;
        scheduleAutoplay();
      },
      { threshold: [0, 0.25] }
    );
    observer.observe(slider.root);
  }

  return {
    destroy: () => {
      destroyed = true;
      clearAutoplay();
      listeners.abort();
      observer?.disconnect();
      delete section.dataset.sliderScrubbing;
    },
  };
};
